import React, { useState, useEffect } from 'react';
import './DataUsage.css';

interface DataUsageRecord {
  id: string;
  deviceId: string;
  clientName: string;
  imei: string;
  iccid: string;
  dataUsed: number;
  dataLimit: number;
  period: string;
  lastUpdated: string;
  status: 'normal' | 'warning' | 'exceeded';
}

const DataUsage: React.FC = () => {
  const [dataRecords, setDataRecords] = useState<DataUsageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchDataUsage();
  }, []);

  const fetchDataUsage = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual external API call
      const mockData: DataUsageRecord[] = [
        {
          id: '1',
          deviceId: 'DEV001',
          clientName: 'Acme Corp',
          imei: '123456789012345',
          iccid: '12345678901234567890',
          dataUsed: 2.5,
          dataLimit: 5.0,
          period: '2024-11',
          lastUpdated: '2024-11-24T01:00:00Z',
          status: 'normal'
        },
        {
          id: '2',
          deviceId: 'DEV002',
          clientName: 'Tech Solutions',
          imei: '123456789012346',
          iccid: '12345678901234567891',
          dataUsed: 4.8,
          dataLimit: 5.0,
          period: '2024-11',
          lastUpdated: '2024-11-24T01:00:00Z',
          status: 'warning'
        },
        {
          id: '3',
          deviceId: 'DEV003',
          clientName: 'Global Logistics',
          imei: '123456789012347',
          iccid: '12345678901234567892',
          dataUsed: 6.2,
          dataLimit: 5.0,
          period: '2024-11',
          lastUpdated: '2024-11-24T01:00:00Z',
          status: 'exceeded'
        }
      ];
      setDataRecords(mockData);
    } catch (error) {
      console.error('Error fetching data usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = dataRecords.filter(record => {
    const matchesFilter = filter === 'all' || record.status === filter;
    const matchesSearch = record.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.deviceId.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         record.imei.includes(searchTerm) ||
                         record.iccid.includes(searchTerm);
    return matchesFilter && matchesSearch;
  });

  const getUsagePercentage = (used: number, limit: number) => {
    return Math.min((used / limit) * 100, 100);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'normal': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'exceeded': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const getProgressColor = (status: string) => {
    switch (status) {
      case 'normal': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'exceeded': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="data-usage-container">
        <div className="loading-spinner">Loading data usage...</div>
      </div>
    );
  }

  return (
    <div className="data-usage-container">
      <div className="data-usage-header">
        <h1 className="page-title">Data Usage Monitor</h1>
        <p className="page-subtitle">Track device data consumption across all clients</p>
      </div>

      <div className="data-usage-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by client, device ID, IMEI, or ICCID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-btn ${filter === 'normal' ? 'active' : ''}`}
            onClick={() => setFilter('normal')}
          >
            Normal
          </button>
          <button
            className={`filter-btn ${filter === 'warning' ? 'active' : ''}`}
            onClick={() => setFilter('warning')}
          >
            Warning
          </button>
          <button
            className={`filter-btn ${filter === 'exceeded' ? 'active' : ''}`}
            onClick={() => setFilter('exceeded')}
          >
            Exceeded
          </button>
        </div>
      </div>

      <div className="data-usage-grid">
        {filteredRecords.map((record) => (
          <div key={record.id} className="usage-card">
            <div className="card-header">
              <h3 className="client-name">{record.clientName}</h3>
              <span className={`status-badge ${getStatusColor(record.status)}`}>
                {record.status.toUpperCase()}
              </span>
            </div>
            
            <div className="device-info">
              <p><strong>Device:</strong> {record.deviceId}</p>
              <p><strong>IMEI:</strong> {record.imei}</p>
              <p><strong>ICCID:</strong> {record.iccid}</p>
            </div>

            <div className="usage-progress">
              <div className="usage-stats">
                <span>{record.dataUsed.toFixed(1)} GB / {record.dataLimit.toFixed(1)} GB</span>
                <span>{getUsagePercentage(record.dataUsed, record.dataLimit).toFixed(0)}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className={`progress-fill ${getProgressColor(record.status)}`}
                  style={{ width: `${getUsagePercentage(record.dataUsed, record.dataLimit)}%` }}
                ></div>
              </div>
            </div>

            <div className="card-footer">
              <small>Period: {record.period}</small>
              <small>Updated: {new Date(record.lastUpdated).toLocaleDateString()}</small>
            </div>
          </div>
        ))}
      </div>

      {filteredRecords.length === 0 && (
        <div className="no-results">
          <p>No data usage records found matching your criteria.</p>
        </div>
      )}
    </div>
  );
};

export default DataUsage;
